"use client";
import BottomSheet from "@/components/ui/BottomSheet";
import { useIsMobile } from "@/hooks/useIsMobile";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";

const IconDropdown = ({
  children,
  iconClassName,
  buttonClassName,
  dropdownClassName,
  itemClassName,
  icon = "ellipsis-vertical",
  iconPrefix = "fak",
  onItemClick,
  items = [],
  renderItem,
  bottomSheetTitle,
}) => {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Scroll veya resize olunca dropdown'ı kapat
  useEffect(() => {
    if (!isOpen) return;

    const handleScrollOrResize = () => setIsOpen(false);

    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen]);

  const handleToggle = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const estimatedDropdownHeight = items.length * 58 + 16;
        const spaceBelow = viewportHeight - rect.bottom;

        if (spaceBelow < estimatedDropdownHeight) {
          // Yukarı aç
          setDropdownStyle({
            position: "fixed",
            bottom: viewportHeight - rect.top + 8,
            right: window.innerWidth - rect.right,
          });
        } else {
          // Aşağı aç
          setDropdownStyle({
            position: "fixed",
            top: rect.bottom + 8,
            right: window.innerWidth - rect.right,
          });
        }
      }

      setIsOpen((prev) => !prev);
    },
    [items.length],
  );

  const handleItemClick = useCallback(
    (event, item, index) => {
      event.preventDefault();
      event.stopPropagation();
      if (onItemClick) {
        onItemClick(item, index);
      }
      setIsOpen(false);
    },
    [onItemClick],
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={clsx(
          "flex cursor-pointer items-center justify-center",
          buttonClassName,
        )}
        aria-label="Menu"
      >
        <FontAwesomeIcon
          icon={byPrefixAndName[iconPrefix][icon]}
          className={clsx("text-gray-600", iconClassName)}
        />
      </button>

      {isOpen && !isMobile && (
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className={clsx(
            "shadow-xxl z-50 min-w-[200px] rounded-lg bg-white",
            dropdownClassName,
          )}
        >
          {items.map((item, index) => (
            <>
              <div
                key={index}
                onClick={(event) => handleItemClick(event, item, index)}
                className={clsx(
                  "cursor-pointer px-4 py-[19px] transition-colors hover:bg-gray-50",
                  "text-base/21 font-normal",
                  index === 0 && "rounded-t-lg",
                  index === items.length - 1 && "rounded-b-lg",
                  itemClassName,
                )}
              >
                {renderItem ? renderItem(item, index) : item.label || item}
              </div>
              {index !== items.length - 1 && (
                <div className="mx-4 h-px bg-gray-200"></div>
              )}
            </>
          ))}
          {children}
        </div>
      )}

      <BottomSheet
        isOpen={isOpen && isMobile}
        onClose={handleClose}
        title={bottomSheetTitle}
        contentClassName="p-0"
      >
        <div className="flex flex-col">
          {items.map((item, index) => (
            <div
              key={index}
              onClick={(event) => handleItemClick(event, item, index)}
              className={clsx(
                "border-border-weak cursor-pointer border-b px-4 py-3 last:border-b-0 active:bg-gray-50",
                itemClassName,
              )}
            >
              {renderItem ? renderItem(item, index) : item.label || item}
            </div>
          ))}
          {children}
        </div>
      </BottomSheet>
    </div>
  );
};

export default IconDropdown;
